import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { QuestionAnsweringCommands } from '~/constants/commands'
import { commandTypeFromJSON, DBAgentTaskRequestResponse } from '~/protos/agents'
import { serverSideProps } from '~/utils/ssr'
import { initialConversationUUID } from './utils'

type SliceState = {
  messages: Record<string, Message[]>
  task_objs: Record<string, TaskObject | null>
}

function buildInitialState(): SliceState {
  const uuid = initialConversationUUID()
  const task = serverSideProps.task || null
  let messages: Message[] = []
  const commandId = commandTypeFromJSON(task?.cmd_type)
  if (task != null && QuestionAnsweringCommands.includes(commandId)) {
    messages = serverSideProps.messages || []
  }
  return {
    messages: {
      [uuid]: messages
    },
    task_objs: { [uuid]: task }
  }
}

const initialState: SliceState = buildInitialState()

export const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setMessages(state, action: PayloadAction<{ uuid: string; messages: Message[] }>) {
      const { uuid, messages } = action.payload
      state.messages[uuid] = messages
    },
    appendMessage(state, action: PayloadAction<{ uuid: string; message: Message }>) {
      const { message, uuid } = action.payload
      const messages = state.messages[uuid] || []
      messages.push(message)
    },
    updateLatestMessage(state, action: PayloadAction<{ uuid: string; message: Message }>) {
      const { uuid, message } = action.payload
      const messages = state.messages[uuid] || []
      const lastestMessage = messages[messages.length - 1]
      if (lastestMessage) {
        lastestMessage.role = message.role
        lastestMessage.text = message.text
        lastestMessage.blocks = message.blocks
      }
    },
    updateTaskMessage(state, action: PayloadAction<{ uuid: string; message: Message }>) {
      const { message: payload, uuid } = action.payload
      const messages = state.messages[uuid] || []

      const taskMessageIndex = messages.findIndex(message => {
        const taskBlock = message.blocks?.find(block => block.type === 'task')
        if (taskBlock == null) return null
        const taskId = taskBlock.fields!.task_id
        return taskId === payload.blocks![0].fields!.task_id
      })
      messages[taskMessageIndex] = payload
    },
    popMessages(state, action: PayloadAction<{ uuid: string; count: number | undefined }>) {
      const { uuid, count = 1 } = action.payload
      const messages = state.messages[uuid] || []
      for (let i = 0; i < count; i++) {
        messages.pop()
      }
    },
    setTaskObj(state, action: PayloadAction<{ task: TaskObject | null; uuid: string }>) {
      const { task = null, uuid } = action.payload
      if (task == null) {
        state.task_objs[uuid] = task
        return
      }
      const { details, ...rest } = task
      const taskObj = Object.assign(rest, { details: DBAgentTaskRequestResponse.fromJSON(details) })
      state.task_objs[uuid] = taskObj
    }
  }
})

export const {
  appendMessage,
  updateLatestMessage,
  popMessages,
  setMessages,
  updateTaskMessage,
  setTaskObj
} = chatSlice.actions

export default chatSlice.reducer
